<template>
  <div class="player-idle-phase">
    <IdleChromeShell :compact-layout="false" :hibernate="false" :screensaver-motion="idleScreensaverMotion">
      <NetworkDisconnected v-if="!networkStatus.connected" />
      <WaitingForRegistrationStatus v-else-if="!registrationReceived" />
      <div
        v-else-if="typeof tenant === 'object' && tenant !== null"
        class="flex flex-col items-center text-center gap-[2.5vh]"
        style="max-width: min(80vw, 90vh);"
      >
        <div
          v-if="feedback.tone === 'error'"
          class="flex items-start gap-[1vw] px-[2vw] py-[1.25vh] rounded-lg bg-red-500/15 border border-red-500/35 text-red-200"
          style="font-size: clamp(0.65rem, 1.1vw, 1rem);"
        >
          <TriangleAlert class="size-4 shrink-0 mt-0.5" />
          <span class="text-left break-words">{{ feedback.detail ?? feedback.headline }}</span>
        </div>
        <template v-else>
          <div
            v-if="feedback.tone === 'warning'"
            class="flex items-start gap-[1vw] px-[2vw] py-[1.25vh] rounded-lg border border-amber-400/35 bg-amber-500/15 text-left text-amber-100"
            style="font-size: clamp(0.65rem, 1.05vw, 0.95rem); max-width: min(80vw, 36rem);"
          >
            <TriangleAlert class="size-4 shrink-0 mt-0.5 opacity-90" />
            <div class="flex min-w-0 flex-col gap-[0.6vh]">
              <span class="font-medium tracking-wide">{{ feedback.headline }}</span>
              <span
                v-if="feedback.detail"
                class="text-white/70 font-normal tracking-normal normal-case"
                style="letter-spacing: 0.01em; line-height: 1.35;"
              >
                {{ feedback.detail }}
              </span>
            </div>
          </div>
          <div
            v-else
            class="flex flex-col items-center gap-[1.5vh]"
            style="max-width: min(80vw, 90vh);"
          >
            <PlayerIdleStatusLine>{{ feedback.headline }}</PlayerIdleStatusLine>
            <p
              v-if="feedback.detail"
              class="text-white/55 font-normal text-center px-[2vw]"
              style="font-size: clamp(0.6rem, 0.95vw, 0.85rem); line-height: 1.4; max-width: min(80vw, 32rem);"
            >
              {{ feedback.detail }}
            </p>
          </div>
        </template>
      </div>
    </IdleChromeShell>
  </div>
</template>

<script lang="ts" setup>
import { aspectRatioFromViewportDimensions } from '@lucimark/shared/helpers/aspectRatioFromPlaylistFrameLayouts'
import { playerGlassStateDetail, playerGlassStateHeadline } from '@lucimark/shared/helpers/playerGlassState'
import { effectiveViewportDimensionsForAspect } from '@lucimark/shared/helpers/playerScreenAspectRatio'
import { TriangleAlert } from 'lucide-vue-next'
import { computed, toRefs, unref } from 'vue'
import IdleChromeShell from '@/components/IdleChromeShell.vue'
import NetworkDisconnected from '@/components/NetworkDisconnected.vue'
import PlayerIdleStatusLine from '@/components/PlayerIdleStatusLine.vue'
import WaitingForRegistrationStatus from '@/components/WaitingForRegistrationStatus.vue'
import usePlayer from '@/composables/usePlayer'
import { bootSplashVisible } from '@/helpers/bootSplash'
import { cssEffectiveScreenRotationFromState } from '@/helpers/cssEffectiveScreenRotation'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import { resolvePlaybackGeoPointForSchedule } from '@/helpers/playbackAssignmentGeo'
import { resolvePlayerIdleWaitingFeedback } from '@/helpers/resolvePlayerIdleWaitingFeedback'

const { state } = usePlayer()
const {
  tenant,
  registrationCode,
  networkStatus,
  campaignLoadError,
  campaign,
  queue,
  skippedUncachedFrameCount,
  capacityTier,
  screenSize,
  assignedMasterPlaylistUrn,
  geolocationPosition
} = toRefs(state)

const registrationReceived = computed(
  () => unref(registrationCode) !== null || unref(tenant) !== null
)

const feedback = computed(() => {
  const t = unref(tenant)
  const excludeFramesWithVideo
    = unref(capacityTier) === 'low'
      && t != null
      && t.playerAllowVideoOnLowCapacity !== true

  const restrict = t != null && t.playerRestrictPlaybackToDeviceAspectRatio !== false
  const { width, height } = unref(screenSize)
  const { width: ew, height: eh } = effectiveViewportDimensionsForAspect(
    width,
    height,
    cssEffectiveScreenRotationFromState(state)
  )
  const viewportAspect = aspectRatioFromViewportDimensions(ew, eh)
  const allowedAspectRatios = restrict ? new Set([viewportAspect]) : null

  const resolved = resolvePlayerIdleWaitingFeedback({
    campaignLoadError: unref(campaignLoadError),
    campaign: unref(campaign),
    queueLength: unref(queue).length,
    excludeFramesWithVideo,
    allowedAspectRatios,
    portalBookViewportAspect: t != null ? viewportAspect : null,
    operatorMasterPlaylistUrn: unref(assignedMasterPlaylistUrn),
    referenceInstant: getLocalAsUtcInstant(),
    geoPoint: resolvePlaybackGeoPointForSchedule(
      unref(geolocationPosition),
      unref(campaign)
    )
  })
  if (
    unref(queue).length < 1
    && unref(skippedUncachedFrameCount) > 0
    && resolved.tone === 'info'
  ) {
    return {
      ...resolved,
      headline: playerGlassStateHeadline('waiting_for_publication', 'pt'),
      detail: playerGlassStateDetail('waiting_for_publication', 'pt')
    }
  }
  return resolved
})

/** Logo + status drift while waiting for playlists (burn-in protection on static idle). */
const idleScreensaverMotion = computed(
  () =>
    !bootSplashVisible.value
    && unref(networkStatus).connected
    && unref(registrationReceived)
)
</script>
